import React, { useEffect, useState } from "react";
import { Tag, Info, Folder, ShieldCheck, CalendarDays, MapPin, PackageOpen } from "lucide-react";
import { useProductInfo } from "../../hooks/useProductInfo";
import { formatConditionLabel } from "../../utils/ProductDetailsV2Utils";
import { reverseGeocodeCity } from "../../../../../shared/PlaceSearch/helpers/PlacesSearchHelper";
import { buildGoogleMapsLink } from "../../../../../../services/helpers/ContactHelper";
import "./styles/SpecsCard.css";

function formatRelativeDate(dateLike: unknown): string {
  try {
    const d = typeof dateLike === "string" ? new Date(dateLike) : (dateLike as Date);
    if (!d || Number.isNaN(d.getTime())) return "-";
    const ist = new Date(d.getTime() + (5 * 60 + 30) * 60_000);
    const days = Math.floor(Math.abs(Date.now() - ist.getTime()) / 86_400_000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return `${days} days ago`;
  } catch { return "-"; }
}

function fmt(v: unknown): string {
  if (v == null || v === "") return "-";
  const s = String(v).trim();
  return s === "" || s.toLowerCase() === "n/a" ? "-" : s;
}

const SpecsCard: React.FC = () => {
  const { age, category, condition, postedDate, location, lat, lng, specAttributes } = useProductInfo();
  const [resolvedLocation, setResolvedLocation] = useState<string | undefined>(location || undefined);

  useEffect(() => {
    if (location) { setResolvedLocation(location); return; }
    if (lat == null || lng == null) return;
    reverseGeocodeCity(lat, lng)
      .then((r: any) => setResolvedLocation(r.formattedAddress ? `${r.displayName} — ${r.formattedAddress}` : r.displayName))
      .catch(() => setResolvedLocation(`${lat.toFixed(4)}°N, ${lng.toFixed(4)}°E`));
  }, [lat, lng, location]);

  const conditionDisplay = formatConditionLabel(condition) || fmt(condition);

  const detailItems = [
    { icon: <Info size={16} />, label: "Age", value: fmt(age) },
    { icon: <Folder size={16} />, label: "Category", value: fmt(category) },
    { icon: <ShieldCheck size={16} />, label: "Condition", value: conditionDisplay },
    { icon: <CalendarDays size={16} />, label: "Posted", value: formatRelativeDate(postedDate) },
  ].filter(({ value }) => value && value !== "-" && value.toLowerCase() !== "not specified");

  const hasLocation = (lat != null && lng != null) || !!resolvedLocation;

  return (
    <div className="spcv2 glass-card">

      {/* Specifications from backend details */}
      {specAttributes.length > 0 ? (
        <div className="spcv2__specs-section">
          <h4 className="spcv2__section-title">
            <Tag size={14} />
            <span>Specifications</span>
          </h4>
          <div className="spcv2__attr-grid">
            {specAttributes.map((attr) => {
              if (attr.tags && attr.tags.length > 0) {
                return (
                  <div key={attr.label} className="spcv2__attr-row spcv2__attr-row--tags">
                    <span className="spcv2__attr-label">{attr.label}</span>
                    <div className="spcv2__tag-chips">
                      {attr.tags.map((tag, i) => (
                        <span key={i} className="spcv2__tag-chip">{tag}</span>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <div key={attr.label} className="spcv2__attr-row">
                  <span className="spcv2__attr-label">{attr.label}</span>
                  <span className="spcv2__attr-divider" />
                  <span className={`spcv2__attr-value${attr.value === "-" ? " spcv2__attr-value--empty" : ""}`}>
                    {attr.value}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="spcv2__empty">
          <PackageOpen size={28} strokeWidth={1.5} />
          <p>No specifications available</p>
        </div>
      )}

      {/* Product Details — only shown if at least one value exists */}
      {detailItems.length > 0 && (
        <>
          <div className="spcv2__divider" />
          <h4 className="spcv2__section-title spcv2__section-title--details">Product Details</h4>
          <div className="spcv2__feature-grid">
            {detailItems.map(({ icon, label, value }) => (
              <div key={label} className="spcv2__feature-item">
                <div className="spcv2__feature-icon">{icon}</div>
                <div className="spcv2__feature-text">
                  <span className="spcv2__feature-label">{label}</span>
                  <span className="spcv2__feature-value">{value}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Location banner */}
      {hasLocation && (
        <div className="spcv2__location">
          <div className="spcv2__location-left">
            <div className="spcv2__location-icon"><MapPin size={18} /></div>
            <div className="spcv2__location-text">
              <span className="spcv2__location-label">Listed from</span>
              <span className="spcv2__location-name">
                {resolvedLocation || `${lat?.toFixed(4)}°N, ${lng?.toFixed(4)}°E`}
              </span>
            </div>
          </div>
          {lat != null && lng != null && (
            <a
              className="spcv2__maps-btn"
              href={buildGoogleMapsLink(lat, lng)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Maps
            </a>
          )}
        </div>
      )}

    </div>
  );
};

export default SpecsCard;
